/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISecretsResolver,
	ITranslationResolver,
	MEDIA_RESOLUTIONS,
	RUNTIME_CONTEXT_MODE,
	TGenericBlueprintSchema,
	TGenericDataSourceDefinition,
	TGenericFunctionDefinition
} from "@adapptio/lib-blueprint";
import { TGenericViewComponentDefinition } from "@adapptio/lib-components";
import {
	ComponentResolver,
	CoreComponentEventNodeTypes,
	createCoreComponentEventHandlers,
	DataSourceResolver,
	FunctionResolver,
	IActionDelegateResolver,
	IActionParams,
	IComponentDefRegistry,
	ICoreComponentEventHandlersOpts,
	IDataSourceDefRegistry,
	IDisposableDependency,
	IEventHandlerLogFn,
	IFunctionDefRegistry,
	IThemeProvider,
	ITranslationManager,
	registerCoreDataSources,
	TranslationManager
} from "@adapptio/lib-core";
import { ItemRegistry } from "@adapptio/lib-shared";
import { IEditorApiService } from "../api/IEditorApiService";
import { BrowserLogger } from "../common/BrowserLogger";
import { EnvironmentService } from "../environment/EnvironmentService";
import { IEditor } from "../IEditor";
import { ResourceService } from "../resources/ResourceService";
import { EditorActionRepository } from "./EditorActionRepository";
import {
	EditorRenderContext,
	IEditorRenderContextOpts_RootView,
	TEditorRenderContextGetCmpList
} from "./EditorRenderContext";
import { EditorRoutingManager } from "./EditorRoutingManager";
import { ActionRefResolver } from "./Resolvers/ActionRefResolver";
import { ThemeRefResolver } from "./Resolvers/ThemeRefResolver";
import { ComponentEventResolver } from "./Resolvers/ComponentEventResolver";
import { IconNameResolver } from "./Resolvers/IconNameResolver";
import { SecretsEditorResolver } from "./Resolvers/SecretsEditorResolver";
import { ViewInstanceResolver } from "./Resolvers/ViewInstanceResolver";
import { ViewRefResolver } from "./Resolvers/ViewRefResolver";
import { IAppInfo } from "../common/AppInfo";
import { AppConfigService } from "../config/AppConfigService";
import { EditorThemeProvider } from "./EditorThemeProvider";

/**
 * Function to register runtime dependencies (component, functions, etc.)
 */
export type TRuntimeRegisterFunction = (runtimeService: RuntimeService) => void;

/**
 * Options to create editor render context
 */
export interface IRuntimeRenderContextCreateOpts<TSchema extends TGenericBlueprintSchema> {
	mode: RUNTIME_CONTEXT_MODE;
	mediaResolution: keyof typeof MEDIA_RESOLUTIONS;
	routingManager: EditorRoutingManager;
	eventLogFn: IEventHandlerLogFn;
	getComponentListSpec: TEditorRenderContextGetCmpList<TSchema>;
	/**
	 * Root view - if specified the root view runtime context will be created,
	 * otherwise runtime context with dummy render functions will be created instead.
	 */
	rootView?: IEditorRenderContextOpts_RootView;
	withPrivateConstants?: boolean;
}

export interface IRuntimeServiceBaseResolvers {
	component: ComponentResolver;
	function: FunctionResolver;
	dataSource: DataSourceResolver;
	iconName: IconNameResolver;
	actionRef: ActionRefResolver;
	themeRef: ThemeRefResolver;
	viewRef: ViewRefResolver;
	translate: ITranslationResolver;
	viewInstance: ViewInstanceResolver;
	actionDelegate: IActionDelegateResolver;
	secret: ISecretsResolver;
	/** Note: the following resolvers must be set individually for contexts */
	// navigation: INavigationResolver; // via EditorRoutingManager
	// route: RouteResolver; // via EditorRoutingManager
	// componentEvent: IComponentEventResolver;
}

/**
 * Service options
 */
export interface IRuntimeServiceOpts {
	registerFn: TRuntimeRegisterFunction;
}

/**
 * Service that provides blueprint design/compile/runtime contexts will all necessary resolvers
 */
export class RuntimeService implements IDisposableDependency {
	/** Logger facility */
	private logger: BrowserLogger;

	/* Dependency services */
	private resourceSvc: ResourceService;
	private apiSvc: IEditorApiService;
	private envSvc: EnvironmentService;

	/* Registries */
	private componentRegistry: IComponentDefRegistry;
	private functionRegistry: IFunctionDefRegistry;
	private dataSourceRegistry: IDataSourceDefRegistry;

	/** Translation manager instance */
	private translationManager: ITranslationManager;

	/** Action repository */
	private actionRepository: EditorActionRepository;

	/** Base resolvers */
	private baseResolvers: IRuntimeServiceBaseResolvers;

	private readonly themeProvider: IThemeProvider;

	/** App info */
	private getAppInfo: () => IAppInfo;

	/** App config service */
	private appConfigSvc: AppConfigService;

	/**
	 * Runtime Service Constructor
	 *
	 * @param dc Dependency Container
	 * @param opts Configuration options
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public constructor(private dc: IEditor, opts: IRuntimeServiceOpts) {
		this.logger = dc.get("logger").facility("RuntimeService");
		this.resourceSvc = dc.get("resourceService");
		this.apiSvc = dc.get("apiService");
		this.envSvc = dc.get("environmentService");

		this.getAppInfo = () => dc.get("appInfo");

		this.appConfigSvc = dc.get("appConfigService");

		this.themeProvider = new EditorThemeProvider({
			apiService: this.apiSvc,
			appConfigService: this.appConfigSvc,
			resourceService: this.resourceSvc
		});

		// Create internal instances for resolver
		this.componentRegistry = new ItemRegistry<TGenericViewComponentDefinition>();
		this.functionRegistry = new ItemRegistry<TGenericFunctionDefinition>();
		this.dataSourceRegistry = new ItemRegistry<TGenericDataSourceDefinition>();

		this.translationManager = new TranslationManager({});
		this.actionRepository = new EditorActionRepository(this.apiSvc, {
			checkTimeoutMs: 1000,
			delegateCacheTimeoutMs: 10000
		});

		// Register core data sources
		registerCoreDataSources(this.dataSourceRegistry);

		// Register dependencies
		opts.registerFn(this);

		// Create resolvers
		this.createBaseResolvers();
	}

	/**
	 * Returns component registry
	 */
	public getComponentRegistry(): IComponentDefRegistry {
		return this.componentRegistry;
	}

	/**
	 * Returns function registry
	 */
	public getFunctionRegistry(): IFunctionDefRegistry {
		return this.functionRegistry;
	}

	/**
	 * Returns data source registry
	 */
	public getDataSourceRegistry(): IDataSourceDefRegistry {
		return this.dataSourceRegistry;
	}

	/**
	 * Returns translation manager
	 */
	public getTranslationManager(): ITranslationManager {
		return this.translationManager;
	}

	/**
	 * Returns action repository
	 */
	public getActionRepository(): EditorActionRepository {
		return this.actionRepository;
	}

	/**
	 * Creates base resolvers
	 */
	private createBaseResolvers() {
		this.baseResolvers = {
			component: new ComponentResolver(this.componentRegistry),
			function: new FunctionResolver(this.functionRegistry),
			dataSource: new DataSourceResolver(this.dataSourceRegistry),
			iconName: new IconNameResolver(this.resourceSvc),
			secret: new SecretsEditorResolver(),
			actionRef: new ActionRefResolver(this.resourceSvc),
			themeRef: new ThemeRefResolver(this.resourceSvc),
			viewRef: new ViewRefResolver(this.resourceSvc),
			translate: {
				translate: (
					term: string,
					vars?: { [K: string]: string | number | boolean },
					fallbackValue?: string,
					domain?: string
				) => {
					// @todo Use language from identity
					return this.translationManager.translate(
						domain || "app",
						"en_US",
						term,
						vars,
						fallbackValue
					);
				}
			},
			viewInstance: new ViewInstanceResolver(this.logger, this.apiSvc, this.resourceSvc),
			actionDelegate: {
				getDelegate: (actionId: string, params: IActionParams) => {
					return this.actionRepository.getDelegate(actionId, params);
				}
			}
		};
	}

	/**
	 * Initializes services
	 */
	public async init(): Promise<void> {
		// Do nothing
	}

	/**
	 * Returns base resolvers
	 */
	public getBaseResolvers(): IRuntimeServiceBaseResolvers {
		return this.baseResolvers;
	}

	/**
	 * Create a component event resolver for core events
	 *
	 * @param config Core event handlers config
	 */
	public createCoreComponentEventResolver(config: ICoreComponentEventHandlersOpts): ComponentEventResolver {
		const eventHandlers = createCoreComponentEventHandlers(config);
		return new ComponentEventResolver(CoreComponentEventNodeTypes, eventHandlers);
	}

	/**
	 * Creates a routing manager
	 *
	 * @param defaultPath Default path
	 */
	public createRoutingManager(defaultPath?: string): EditorRoutingManager {
		return new EditorRoutingManager(this.resourceSvc, defaultPath);
	}

	/**
	 * Returns am editor render context
	 *
	 * @param opts Create options
	 */
	public createRenderContext<TSchema extends TGenericBlueprintSchema>(
		opts: IRuntimeRenderContextCreateOpts<TSchema>
	): EditorRenderContext<TSchema> {
		return new EditorRenderContext({
			mode: opts.mode,
			mediaResolution: opts.mediaResolution,
			baseResolvers: this.baseResolvers,
			componentRegistry: this.componentRegistry,
			actionRepository: this.actionRepository,
			routingManager: opts.routingManager,
			translationManager: this.translationManager,
			environmentService: this.envSvc,
			iconPackageUrlMapping: this.resourceSvc.getIconPackUrlMapping(),
			eventLogFn: opts.eventLogFn,
			getComponentListSpec: opts.getComponentListSpec,
			rootView: opts.rootView,
			themeProvider: this.themeProvider,
			appInfo: this.getAppInfo(),
			appConfigService: this.appConfigSvc,
			withPrivateConstants: opts.withPrivateConstants
		});
	}

	/**
	 * Disposes service
	 */
	public async dispose(): Promise<void> {
		// Dispose resolvers
		this.baseResolvers.actionRef.dispose();
		this.baseResolvers.viewRef.dispose();
		this.baseResolvers.iconName.dispose();
		this.baseResolvers.viewInstance.dispose();

		// Dispose action repository
		this.actionRepository.dispose();
	}
}
